import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"

// components
import LabelMenu from "../components/LabelMenu/LabelMenu"
import ArrayFieldItem from "./ArrayFieldItem"
import { Popover, Tooltip, Skeleton, Flex } from "antd"
import { PrimeButton } from "components-design-system"

// utils/helpers
import { registryValidMsgs } from "utilities/registryValidations"

// context
import useAuthoringViewStore from "../../hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringActions from "../../hooks/actions/useAuthoringActions"
import useAuthoringValidationsStore from "../../hooks/stores/useAuthoringValidationsStore"
import useAuthoringDataStore from "../../hooks/stores/useAuthoringDataStore"
import useAuthoringStore from "../../hooks/stores/useAuthoringStore"
import useAuthoringTranslations from "../../hooks/stores/useAuthoringTranslations"

// styles
import "./ArrayFieldTemplate.scss"

const ArrayFieldTemplate = (props) => {
  const [showMenu, setShowMenu] = useState(false)
  const [fieldComments, setFieldComments] = useState([])
  const { uiSchema, idSchema, canAdd, onAddClick } = props
  const [fieldLoading, setFieldLoading] = useState(false)
  const [isRootArray, setIsRootArray] = useState(false)

  const {
    isProtocolOmDisabled,
    setIsProtocolOmDisabled,
    isProtocolOmDisabledOverriden,
    setIsProtocolOmDisabledOverriden,
  } = useAuthoringStore(
    useShallow((state) => ({
      isProtocolOmDisabled: state.isProtocolOmDisabled,
      setIsProtocolOmDisabled: state.setIsProtocolOmDisabled,
      isProtocolOmDisabledOverriden: state.isProtocolOmDisabledOverriden,
      setIsProtocolOmDisabledOverriden: state.setIsProtocolOmDisabledOverriden,
    }))
  )

  const { readOnly } = useAuthoringViewStore(useShallow((state) => ({ readOnly: state.readOnly })))

  const { openComment, setOpenComment, commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      setOpenComment: state.setOpenComment,
      commentsMap: state.commentsMap,
    }))
  )

  const { registryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      registryValidations: state.registryValidations,
    }))
  )

  const { formData, translationsData, setTranslationsData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      translationsData: state.translationsData,
      setTranslationsData: state.setTranslationsData,
    }))
  )

  const { scrollToField } = useAuthoringActions()

  const { isTranslationActive, selectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      isTranslationActive: state.isTranslationActive,
      selectedLanguage: state.selectedLanguage,
    }))
  )

  const options = uiSchema["ui:options"]
  const jpath = idSchema.$id.split(".").join("_")

  let title = "Title"
  let arrayItemTitle = "Item Title"
  let addButtonTitle = "Add Item"
  let classNames = {}
  let canCopy = false
  let canCopyOutcomeMeasure = false
  let tooltip, tooltipUrl, tooltipUrlTitle
  let showIndex = options?.showIndex
  let canCopyCtgOutcomeMeasures = false

  useEffect(() => {
    if (commentsMap) {
      if (commentsMap[jpath]) {
        let fieldComments = commentsMap[jpath]
        setFieldComments(fieldComments)
      } else {
        setFieldComments([])
      }
    }
  }, [commentsMap])

  useEffect(() => {
    if (openComment && jpath === openComment) {
      scrollToField(jpath)
    }
  }, [openComment])

  if (options) {
    title = options.title
    arrayItemTitle = options.arrayItemTitle
    addButtonTitle = options.addButtonTitle
    classNames = options.classNames
    tooltip = options.tooltip
    tooltipUrl = options.tooltipUrl
    tooltipUrlTitle = options.tooltipUrlTitle
    canCopy = options.canCopy
    canCopyOutcomeMeasure = options.canCopyOutcomeMeasure
    canCopyCtgOutcomeMeasures = options.canCopyCtgOutcomeMeasures
  }

  if (isTranslationActive && options?.translations?.[selectedLanguage]) {
    const langTranslation = options?.translations?.[selectedLanguage]

    if (langTranslation?.title) {
      title += ` (${langTranslation.title})`
    }
    if (langTranslation?.arrayItemTitle) {
      arrayItemTitle += ` (${langTranslation.arrayItemTitle})`
    }
    if (langTranslation?.addButtonTitle) {
      addButtonTitle += ` (${langTranslation.addButtonTitle})`
    }
  }

  useEffect(() => {
    if (jpath === "root_protocol_study_outcome_measures_outcome_measures") setIsRootArray(true)
  }, [jpath])

  useEffect(() => {
    if (
      jpath.includes("root_protocol_study_outcome_measures_outcome_measures") &&
      isProtocolOmDisabledOverriden === false
    ) {
      let ctgResultsOm = formData?.results?.outcome_measures?.outcome_measures_table?.ctg
      if (
        (ctgResultsOm?.arms?.length > 0 || ctgResultsOm?.outcome_measures?.length > 0) &&
        isProtocolOmDisabled === false
      ) {
        setIsProtocolOmDisabled(true)
      } else if (
        isProtocolOmDisabled &&
        (!ctgResultsOm || (ctgResultsOm?.arms?.length === 0 && ctgResultsOm?.outcome_measures?.length === 0))
      ) {
        setIsProtocolOmDisabled(false)
      }
    }
  }, [formData?.results?.outcome_measures?.outcome_measures_table?.ctg])

  const onOverride = () => {
    setIsProtocolOmDisabled(false)
    setIsProtocolOmDisabledOverriden(true)
  }

  const onAddItem = () => {
    const onCreateTranslations = () => {
      let temp = { ...translationsData }
      if (props.schema?.items?.properties) {
        Object.keys(props.schema.items.properties).forEach((key) => {
          let field = props.schema.items.properties[key]
          if (field.default && field.options.some((item) => item.translations)) {
            let foundOption = field.options.find((option) => option.value === field.default)
            if (foundOption) {
              let index = props.formData.length
              let mainKey = `${props.idSchema.$id.replace("root.", "")}.${index}.`

              if (foundOption.translations) {
                Object.keys(foundOption.translations).forEach((lang) => {
                  temp[lang][mainKey + key] = foundOption.translations[lang]
                })
              }
            }
          }
        })

        setTranslationsData(temp)
      }
    }

    onAddClick()
    onCreateTranslations()
  }

  let content = (
    <div>
      <div className="tooltip-content">{tooltip}</div>
      {tooltipUrl && (
        <div className="tooltip__footer">
          <Link
            to={tooltipUrl}
            target="_blank"
            className="--link"
          >
            {tooltipUrlTitle}
          </Link>
        </div>
      )}
    </div>
  )

  const isArrayDisabled = isProtocolOmDisabled && isRootArray

  if (fieldLoading) return <Skeleton active />

  return (
    <Tooltip
      title={
        "[See Results Section.] Results data entry has begun, if this is not correct please delete results data or use the override option."
      }
      open={isProtocolOmDisabled && isRootArray && showMenu && isProtocolOmDisabledOverriden !== true}
      trigger={"hover"}
    >
      <div
        id={jpath}
        style={{
          display: options?.hide ? "none" : "auto",
        }}
        className={`rjs-array-container ${classNames?.arrayContainer ? classNames.arrayContainer : ""}`}
        onMouseEnter={() => setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
      >
        {title && (
          <Flex
            className="rjs-array-title-container"
            align="center"
            gap={7}
          >
            <div className={`rjs-array-title ${classNames?.arrayTitle ? classNames.arrayTitle : ""}`}>
              {title}
              {/* {tooltip && (
                <Popover
                  content={content}
                  trigger={"hover"}
                >
                  <i className="fal fa-info-circle test"></i>
                </Popover>
              )} */}
              {isArrayDisabled && (
                <Tooltip title={"Manual override"}>
                  <i
                    className="fal fa-pen-square override-disabled-icon"
                    onClick={onOverride}
                  />
                </Tooltip>
              )}
            </div>
            {title && (
              <LabelMenu
                hideFork={true}
                jpath={jpath}
                showMenu={showMenu}
                setShowMenu={setShowMenu}
                fieldComments={fieldComments}
                // canCopyCtgOutcomeMeasures={canCopyCtgOutcomeMeasures}
                setFieldLoading={setFieldLoading}
                formContext={props.formContext}
                {...options}
                label={title}
                showLabel={false}
              />
            )}
          </Flex>
        )}
        <div
          className={`rjs-array-items-container ${
            classNames?.arrayItemsContainer ? classNames.arrayItemsContainer : ""
          }`}
        >
          {props.items.map((element, i) => {
            return (
              <ArrayFieldItem
                key={element.key + i}
                parentProps={props}
                element={element}
                classNames={classNames}
                arrayItemTitle={arrayItemTitle}
                canCopy={canCopy}
                canCopyOutcomeMeasure={canCopyOutcomeMeasure}
                showIndex={showIndex}
                required={options?.required}
                isDisabled={isArrayDisabled}
                formContext={props.formContext}
                setFieldLoading={setFieldLoading}
              />
            )
          })}
        </div>
        {canAdd && readOnly !== true && (
          <PrimeButton
            className="rjs-add-button"
            type="add"
            size="small"
            disabled={isArrayDisabled}
            onClick={onAddItem}
          >
            {addButtonTitle}
          </PrimeButton>
        )}
        {registryValidMsgs(registryValidations, jpath)}
      </div>
    </Tooltip>
  )
}

export default ArrayFieldTemplate
